import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';

const AgentLeadStatusReportPage = ({ dateRangeState, selectedTeams = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');

        const requestData = {
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
        };

        if (selectedTeams && selectedTeams.length > 0) {
          requestData.team_filter = selectedTeams;
        }

        const response = await api.post('/report/agent/lead-status/', requestData);
        const data = response.data || {};
        setRows(data.results || []);
      } catch (error) {
        console.error('Error fetching agent lead status:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRangeState, selectedTeams]);
  return (
    <Box sx={{ flexGrow: 1, pt: 0, pb: 3 }}>
      <Box
        sx={{
          mb: 2,
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}
          >
            Agent-wise Lead Status Report
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
            Lead funnel distribution by touchpoint and outcome for each agent.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
          }}
          // TODO: Wire this to your export logic (CSV / XLSX / PDF)
          onClick={() => {
            console.log('Export Agent Lead Status clicked');
          }}
        >
          Export
        </Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: (theme) => theme.shadows[2],
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[50],
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Agent Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Leads Assigned</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Untouched Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Contacted – Not Connected Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Follow-up Pending Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Site Visit Scheduled Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Site Visit Completed Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Converted (Won)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Lost (Not Interested)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={(theme) => ({
                        height: theme.spacing(7),
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': { border: 0 },
                      })}
                    >
                      <TableCell>{row.agentName}</TableCell>
                      <TableCell align="right">{row.totalLeadsAssigned}</TableCell>
                      <TableCell align="right">{row.untouchedLeads}</TableCell>
                      <TableCell align="right">{row.contactedNotConnectedLeads}</TableCell>
                      <TableCell align="right">{row.followUpPendingLeads}</TableCell>
                      <TableCell align="right">{row.siteVisitScheduledLeads}</TableCell>
                      <TableCell align="right">{row.siteVisitCompletedLeads}</TableCell>
                      <TableCell align="right">{row.leadsConvertedWon}</TableCell>
                      <TableCell align="right">{row.leadsLostNotInterested}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentLeadStatusReportPage;


